import { SourceStrip } from '@ui/source/SourceStrip';
import { recommendationTableRooms } from './PriceRecommendationDrawerContents.utilities';
import { usePricingRights } from '@util/PricingRightsHook';
import React, { PropsWithChildren, useEffect, useState } from 'react';
import { useMutation, useQueries, useQueryClient } from '@tanstack/react-query';
import { PricingAutomationQueryKeys } from '@app/model/pricing-automation/service/PricingAutomationQueryKeys';
import PricingAutomationService from '@app/model/pricing-automation/service/PricingAutomationService';
import { useSelectedCompanyHotel } from '@app/site/secured/rateshopper/rates/provider/useSelectedCompanyHotel';
import _isEmpty from 'lodash/isEmpty';
import Message from '@app/component/message/Message';
import {
	ArrowDownIcon,
	ArrowUpIcon,
	InformationCircleIcon,
} from '@heroicons/react/20/solid';
import PriceRecommendationStateDriver from '@app/model/market_state/PriceRecommendationStateDriver';
import CompanyHotel from '@app/model/company_hotel/CompanyHotel';
import FormButton from '@app/component/form/button/FormButton';
import useModal from '@app/component/overlays/modal/ModalRef';
import TablePlaceholder from '@app/component/placeholder/TablePlaceholder';
import Tabs from '@app/component/tabs/Tabs';
import HotelRoomMappingType, {
	ROOM_MAPPING_TYPE_ORDER,
	sortByRoomMappingType,
} from '@app/model/market_state/HotelRoomMappingType';
import PriceRecommendationStateDriverType from '@app/model/market_state/PriceRecommendationStateDriverType';
import useAppForm from '@app/site/_form';
import { RadioField } from '@app/site/_form/field/RadioField';
import { TextAreaField } from '@app/site/_form/field/TextAreaField';
import { SubmitButton } from '@app/site/_form/button/SubmitButton';
import { NumberField } from '@app/site/_form/field/NumberField';
import { FieldErrors } from '@app/site/_form/field/FieldErrors';
import PriceRecommendationFeedbackInput from '@app/model/pricing-automation/input/PriceRecommendationFeedbackInput';
import PriceRecommendationFeedbackVerdict from '@app/model/pricing-automation/PriceRecommendationFeedbackVerdict';
import { priceRecommendationFeedbackSchema } from '@app/model/pricing-automation/input/PriceRecommendationFeedback.validation';
import PricingArchitectureDerivativeRuleType from '@app/model/pricing-automation/PricingArchitectureDerivativeRuleType';
import Utilities from '@util/Utilities';
import PricingIntegrationModal
	from '@app/site/secured/rateshopper/rates/dashboard/fragment/__market-state/common/recommmendation/overlay/PricingIntegrationModal';
import PriceRecommendationDrawerBookingPace
	from '@app/site/secured/rateshopper/rates/dashboard/fragment/__market-state/common/recommmendation/overlay/PriceRecommendationDrawerBookingPace';
import MarketStateService from '@app/model/market_state/service/MarketStateService';
import { MarketStateGraphQLQueryKeys } from '@app/model/market_state/service/MarketStateGraphQLQueryKeys';
import { PriceRecommendationState } from '@app/model/market_state/PriceRecommendationState';
import PriceRecommendationRoomState from '@app/model/market_state/PriceRecommendationRoomState';
import PricingArchitecture from '@app/model/pricing-automation/PricingArchitecture';
import useToaster from '@app/component/overlays/toaster/ToasterHook';
import { useTranslation } from '@app/component/translation/Translation';
import SubmitPricingResultOutput from '@app/model/pricing-automation/output/SubmitPricingResultOutput';
import SubmitMonthlyPricingResultOutputState from '@app/model/pricing-automation/output/SubmitMonthlyPricingResultOutputState';
import PriceSubmissionBlockedItems from '@app/site/secured/rateshopper/rates/dashboard/fragment/__market-state/common/PriceSubmissionBlockedItems';
import AvailabilityDailyStateProvider, {
	AvailabilityDailyStateContext,
} from '@app/site/secured/rateshopper/rates/dashboard/fragment/__market-state/common/provider/AvailabillityDailyStateProvider';
import {
	useMarketStateTools,
} from '@app/site/secured/rateshopper/rates/dashboard/fragment/__market-state/common/hook/useMarketStateTools';
import { Tooltip } from 'react-tooltip';
import {
	RecommendationLimitLines,
	RecommendationLimitTag,
	useRecommendationLimit,
} from '@app/site/secured/rateshopper/rates/dashboard/fragment/__market-state/common/recommmendation/RecommendationLimitExplanation';
import {
	DRIVER_IMPACT_LEVEL_LABELS,
	findLowestAnchorRoom,
	getDriverImpactLevel,
	roomPriceChangePercent,
	orderRecommendationDrivers,
	selectRecommendationAnalysisSource,
} from './PriceRecommendationDrawerContents.utilities';

const PriceRecommendationDrawerContents: React.FunctionComponent<{ statDate: string, onSubmitPrices: () => any }>
	= ({ statDate, onSubmitPrices }) => {

	const { companyHotel } = useSelectedCompanyHotel();

	const results = useQueries({
		queries: [
			{
				queryKey: PricingAutomationQueryKeys.pricingArchitectures(companyHotel.id),
				queryFn: () => PricingAutomationService.findCompanyHotelPricingArchitectures(companyHotel.id),
				enabled: !!companyHotel?.id,
			}, {
				queryKey: MarketStateGraphQLQueryKeys.priceRecommendationStateForStatDate(companyHotel.id, statDate),
				queryFn: () => MarketStateService.findLatestPriceRecommendationStateForStatDate(companyHotel.id, statDate),
				enabled: !!companyHotel?.id,
			},
		],
	});

	if (results.some(s => s.isLoading)) {
		return <TablePlaceholder />;
	}

	const architectures = results[0].data;
	const recommendationStates = results[1].data;

	if (results.some(s => s.isError) || _isEmpty(architectures) || _isEmpty(recommendationStates)) {
		return <Message state="error">Error loading pricing data</Message>;
	}

	return <AvailabilityDailyStateProvider companyHotel={companyHotel} statDate={statDate}>
		<Contents recommendationStates={recommendationStates!}
		          onSubmitPrices={onSubmitPrices}
		          architectures={architectures!}
		          statDate={statDate} />
	</AvailabilityDailyStateProvider>;

};


const Contents: React.FunctionComponent<{
	recommendationStates: PriceRecommendationState[]
	architectures: PricingArchitecture[],
	onSubmitPrices: () => any,
	statDate: string
}>
	= ({ recommendationStates, architectures, onSubmitPrices, statDate }) => {
	const { companyHotel } = useSelectedCompanyHotel();

	sortByRoomMappingType(recommendationStates, 'roomMappingType');

	const hasMultipleRecommendations = recommendationStates.length > 1;

	const tabs = [];
	if (hasMultipleRecommendations)
		tabs.push({
			label: 'All',
			value: 0,
		});

	recommendationStates.map((reco, idx) => (
		tabs.push({
			label: Utilities.snakeCaseToWords(reco.roomMappingType),
			value: idx + (hasMultipleRecommendations ? 1 : 0),
		})
	));

	tabs.push({
		label: 'Feedback',
		outlined: true,
		value: tabs.length,
	});

	const [selectedTab, setSelectedTab] = React.useState<number>(0);


	const isTabAll = tabs[selectedTab].label === 'All';
	const isTabFeedback = tabs[selectedTab].label === 'Feedback';

	return <div className="mb-6">
		<Tabs
			options={tabs}
			value={selectedTab}
			onChange={(value) => setSelectedTab(typeof value === 'string' ? parseInt(value) : value)}
		/>

		{!isTabFeedback && !isTabAll && <ArchitectureTabContent onSubmitPrices={onSubmitPrices}
		                                                        recommendationState={recommendationStates[selectedTab - (hasMultipleRecommendations ? 1 : 0)]}
		                                                        architecture={architectures.find(arch => arch.id === recommendationStates[selectedTab - (hasMultipleRecommendations ? 1 : 0)].architectureId)!}
		                                                        statDate={statDate} />}

		{isTabAll && <AllArchitecturesContent companyHotel={companyHotel}
		                                      onSubmitPrices={onSubmitPrices}
		                                      recommendationStates={recommendationStates}
		                                      architectures={architectures}
		                                      statDate={statDate} />}

		{isTabFeedback && <FeedbackContent companyHotel={companyHotel} recommendationStates={recommendationStates} />}
	</div>;
};

const FeedbackContent: React.FunctionComponent<{
	companyHotel: CompanyHotel,
	recommendationStates: PriceRecommendationState[]
}> = ({ companyHotel, recommendationStates }) => {

	const [message, setMessage] = useState<{ state: 'success' | 'error', text: string } | undefined>(undefined);

	useEffect(() => {
		if (!message) {
			return;
		}

		const timeout = setTimeout(() => setMessage(undefined), 4000);

		return () => clearTimeout(timeout);
	}, [message]);

	const createFeedbackMutation = useMutation({
		mutationFn: PricingAutomationService.createPricingRecommendationFeedback,
		onSuccess: () => {
			setMessage({ state: 'success', text: 'Thank you. Your feedback reached the pricing team.' });
			form.reset();
		},
		onError: () => {
			setMessage({ state: 'error', text: 'We could not send your feedback. Please try again.' });
		},
	});

	const recommendationOptions = recommendationStates.map(r => ({
		label: Utilities.snakeCaseToWords(r.roomMappingType),
		value: r.id,
	}));

	const verdictOptions = [
		{ label: 'The recommended rate is too high', value: PriceRecommendationFeedbackVerdict.RATE_TOO_HIGH },
		{ label: 'The recommended rate is too low', value: PriceRecommendationFeedbackVerdict.RATE_TOO_LOW },
		{ label: 'Keep the current rate', value: PriceRecommendationFeedbackVerdict.KEEP_CURRENT_RATE },
	];

	const form = useAppForm({
		defaultValues: {
			companyHotelId: companyHotel.id,
			stateId: recommendationStates[0].id,
			verdict: undefined as PriceRecommendationFeedbackVerdict | undefined,
			comment: '',
			suggestedPrice: undefined as number | undefined,
		} as PriceRecommendationFeedbackInput,
		validators: {
			onChange: priceRecommendationFeedbackSchema,
		},
		onSubmit: async ({ value }) => {
			await createFeedbackMutation.mutateAsync({
				input: {
					companyHotelId: value.companyHotelId,
					stateId: value.stateId,
					verdict: value.verdict,
					comment: value.comment.trim(),
					suggestedPrice: value.suggestedPrice,
				},
			}).catch(() => undefined);
		},
	});

	const currency = companyHotel.settings.main.currency;

	return <form.AppForm>
		<form onSubmit={(e) => {
			e.preventDefault();
			e.stopPropagation();
			void form.handleSubmit();
		}}>
			{message && <Message state={message.state} extraClassName="mt-6">{message.text}</Message>}
			<div className="divide-y divide-gray-200 overflow-hidden rounded-md ring-1 ring-black ring-opacity-10 mt-8">
				<div className="px-3 py-3 bg-gray-50">
					<h6 className="text-sm font-semibold text-gray-900">Share Feedback</h6>
				</div>
				<div className="px-3 py-3 text-sm">
					<p className="text-xs font-medium text-gray-700 mb-3">
						Help us improve the recommendation. Pick a room, choose your verdict, and tell us what you would do instead.
					</p>
					<div className="mt-2 grid grid-cols-2 gap-3 sm:grid-cols-4">
						<form.AppField name="stateId">
							{() => recommendationOptions.map(o => <RadioField key={o.value} {...o} variant="button" />)}
						</form.AppField>
					</div>
					<form.Subscribe selector={state => state.values.stateId}>
						{stateId => {
							const state = recommendationStates.find(r => r.id === stateId);
							const anchor = state?.minimumBasePriceRoomStates?.some(room => room.isAnchor)
								? state.findLowestPriceForAnchorLogRoom() : undefined;
							if (!state || !anchor) return null;
							const rate = state.effectiveChangeRate;
							return <p className="mt-3 text-xs text-gray-500">
								{anchor.roomName}: current rate {Utilities.formatAmount(anchor.basePrice, currency, 0)},
								{' '}recommended rate <span className="font-semibold text-gray-900">{Utilities.formatAmount(anchor.finalPrice, currency, 0)}</span>
								{rate !== 0 && <span className={rate > 0 ? 'text-green-600' : 'text-red-600'}> ({rate > 0 ? '+' : ''}{Utilities.formatDouble(rate * 100, 1)}%)</span>}
								{rate === 0 && <span> (no change)</span>}
							</p>;
						}}
					</form.Subscribe>
					<div className="space-y-2 mt-4">
						<form.AppField name="verdict">
							{() => verdictOptions.map(o => {
								return <div key={o.value} className="flex items-center"><RadioField {...o} /></div>;
							})}
						</form.AppField>
					</div>
					<form.AppField name="comment">
						{field => <div className="mt-4">
							<div className="flex flex-wrap">
								<div className="label">Your comment</div>
								<span className="text-xs leading-6 text-gray-500 ml-2 italic">What would you do instead, and why?</span>
							</div>
							<div
								className="mt-0.5 overflow-hidden rounded-lg outline outline-1 -outline-offset-1 outline-gray-300 focus-within:outline focus-within:outline-2 focus-within:-outline-offset-2 focus-within:outline-blue-600">
								<TextAreaField
									rows={4}
									placeholder="Example: 3 days out and 15 rooms still open. A raise will not sell them, so I would keep the rate."
								/>
							</div>
							<FieldErrors meta={field.state.meta} />
						</div>}
					</form.AppField>
					<div className="mt-4 sm:w-80">
						<form.AppField name="suggestedPrice">
							{() => <NumberField
								label="Your rate"
								labelHint={`the rate you would set, optional, in ${Utilities.formatCurrency(currency)}`}
								min={0}
								step={1}
								placeholder="e.g. 175"
							/>}
						</form.AppField>
					</div>

					<div className="mt-4 flex justify-end">
						<SubmitButton state="primary" submitOnClick={false}>Send feedback</SubmitButton>
					</div>
				</div>
			</div>
		</form>
	</form.AppForm>;
};

const AllArchitecturesContent: React.FunctionComponent<{
	companyHotel: CompanyHotel,
	recommendationStates: PriceRecommendationState[],
	architectures: PricingArchitecture[],
	onSubmitPrices: () => void
	statDate: string
}> = ({ companyHotel, recommendationStates, architectures, onSubmitPrices, statDate }) => {
	const analysisSource = selectRecommendationAnalysisSource<
		HotelRoomMappingType,
		PriceRecommendationRoomState,
		PriceRecommendationState
	>(
		recommendationStates,
		ROOM_MAPPING_TYPE_ORDER
	);

	// The Limit Explanation of the room type that feeds the Recommendation Analysis card.
	const limitView = useRecommendationLimit(analysisSource?.state);

	const hasTableRooms = recommendationStates.some(state => recommendationTableRooms(state.minimumBasePriceRoomStates).length > 0);

	return <div className="mt-6 space-y-4">
		<div className="text-sm text-gray-600">
			<p className="font-medium text-gray-900">Complete Rate Overview - All Room Types</p>
			<p>View and manage rates for all room categories in your property.
			</p>
		</div>
		{analysisSource !== undefined && limitView !== undefined &&
			<div className="overflow-hidden rounded-md bg-blue-50 ring-1 ring-blue-500 ring-opacity-30 shadow-sm">
				<div className="px-4 py-3">
					<div className="flex flex-wrap items-center gap-x-2 gap-y-1">
						<RecommendationLimitTag view={limitView} />
						<span className="text-xs text-gray-500">
							{Utilities.snakeCaseToWords(analysisSource.state.roomMappingType)} — the cheapest room type with a recommendation
						</span>
					</div>
					<div className="mt-2">
						<RecommendationLimitLines view={limitView} />
					</div>
				</div>
			</div>}
		{analysisSource
			? <RenderRecommendationAnalysis recommendationState={analysisSource.state} />
			: <RenderNoRecommendationNotice />}
		<PriceRecommendationDrawerBookingPace recommendation={recommendationStates[0]} showTotalsOnly />
		{hasTableRooms && <PriceTableWrapper>
			{recommendationStates.map((recommendationState, idx) => {
				const architecture = architectures.find(arch => arch.id === recommendationState.architectureId)!;
				return <RenderPriceTable key={idx} companyHotel={companyHotel} recommendationState={recommendationState}
				                         architecture={architecture} showRecommendation={recommendationState.isValid} />;
			})}
		</PriceTableWrapper>}
		<div className="flex gap-x-2 justify-end -mt-3">
			<RenderEditPricingButton companyHotel={companyHotel}
			                         onSubmit={onSubmitPrices}
			                         recommendationStates={recommendationStates}
			                         architectures={architectures}
			                         statDate={statDate} />
		</div>
	</div>;
};

const ArchitectureTabContent: React.FunctionComponent<{
	recommendationState: PriceRecommendationState,
	architecture: PricingArchitecture,
	onSubmitPrices: () => any,
	statDate: string
}> = ({ recommendationState, architecture, onSubmitPrices, statDate }) => {
	const { companyHotel } = useSelectedCompanyHotel();
	// The Limit Explanation of a Limited Recommendation: the tag under the rate, the two lines under the card.
	const limitView = useRecommendationLimit(recommendationState);

	if (!recommendationState) return <Message state="error" extraClassName="mt-4">There is no recommendation found for
		this
		architecture</Message>;

	const analysisSource = selectRecommendationAnalysisSource<
		HotelRoomMappingType,
		PriceRecommendationRoomState,
		PriceRecommendationState
	>(
		[recommendationState],
		ROOM_MAPPING_TYPE_ORDER
	);
	const lowestPriceLogRoom = analysisSource?.room;
	const roomChangePercent = lowestPriceLogRoom
		? roomPriceChangePercent(lowestPriceLogRoom.basePrice, lowestPriceLogRoom.finalPrice)
		: 0;
	const hasValidRecommendation = recommendationState.isValid;

	return <div className="mt-4 space-y-4">
		{!analysisSource && <RenderNoRecommendationNotice />}
		{lowestPriceLogRoom &&
			<div className="overflow-hidden rounded-md bg-blue-50 ring-1 ring-blue-500 ring-opacity-30 shadow-sm">
				<div className={`px-4 pt-5 ${limitView !== undefined ? 'pb-3' : 'pb-4'}`}>
					<p className="mb-3 text-xs font-medium text-blue-700 break-words">WebHotelier · {lowestPriceLogRoom.roomName}</p>
					<div className="sm:grid grid-cols-1 gap-4 sm:grid-cols-2 space-y-2 sm:space-y-0">
						<div className="border-r border-blue-200 pr-4">
							<p className="text-sm font-medium text-gray-500">Base rate</p>
							<div className="flex items-baseline">
								<p
									className="text-2xl font-semibold text-gray-900">{Utilities.formatAmount(lowestPriceLogRoom.basePrice, companyHotel.settings.main.currency, 0)}</p>
							</div>
						</div>
						<div><p className="truncate text-sm font-medium text-blue-600">Proposed rate</p>
							<div className="flex items-baseline gap-x-2">
								<p
									className="text-2xl font-semibold text-gray-900">{Utilities.formatAmount(lowestPriceLogRoom.finalPrice, companyHotel.settings.main.currency, 0)}</p>
								<div className="flex items-baseline text-sm font-semibold text-green-600">
									{roomChangePercent !== 0 && <p
										className={`flex items-center text-sm font-semibold ${roomChangePercent > 0 ? 'text-green-600' : 'text-rose-600'}`}>
										{roomChangePercent > 0 ? <ArrowUpIcon className="w-4 h-4" /> :
											<ArrowDownIcon className="w-4 h-4" />}
										<span>{Math.abs(roomChangePercent)}%</span>
									</p>}
								</div>
							</div>
							{limitView?.limited && <div className="mt-1.5 flex items-center">
								<RecommendationLimitTag view={limitView} />
							</div>}
						</div>
					</div>
				</div>
				{limitView !== undefined && <div className="px-4 pt-2.5 pb-3 border-t border-blue-200">
					<RecommendationLimitLines view={limitView} />
				</div>}
			</div>}
		{analysisSource && <RenderRecommendationAnalysis recommendationState={analysisSource.state} />}
		{!!recommendationState.marketStatePromptId &&
			<PriceRecommendationDrawerBookingPace recommendation={recommendationState} />}
		{recommendationTableRooms(recommendationState.minimumBasePriceRoomStates).length > 0 && <PriceTableWrapper>
			<RenderPriceTable companyHotel={companyHotel} recommendationState={recommendationState}
			                  architecture={architecture} showRecommendation={hasValidRecommendation} />
		</PriceTableWrapper>}
		<div className="flex gap-x-2 justify-end -mt-3">
			<RenderEditPricingButton companyHotel={companyHotel}
			                         recommendationStates={[recommendationState]}
			                         architectures={[architecture]}
			                         onSubmit={onSubmitPrices}
			                         statDate={statDate} />
		</div>
	</div>;
};

const DRIVER_ORDER = [
	PriceRecommendationStateDriverType.COMPETITORS,
	PriceRecommendationStateDriverType.MARKET,
	PriceRecommendationStateDriverType.OCCUPANCY,
];

const DRIVER_DETAILS: Record<PriceRecommendationStateDriverType, { label: string, description: string }> = {
	[PriceRecommendationStateDriverType.COMPETITORS]: {
		label: 'Competitors',
		description: 'The effect of the competitor rates on this date. RoomPulse compares the live rates of your comp set against your rate.',
	},
	[PriceRecommendationStateDriverType.MARKET]: {
		label: 'Market',
		description: 'The effect of the market demand on this date. RoomPulse uses the demand score of your market and the rate percentiles.',
	},
	[PriceRecommendationStateDriverType.OCCUPANCY]: {
		label: 'Occupancy',
		description: 'The effect of your own hotel on this date. RoomPulse uses your occupancy and your Booking Pace against the normal pace.',
	},
};

const DRIVER_SEGMENT_COLORS = ['bg-blue-300', 'bg-blue-400', 'bg-blue-500', 'bg-blue-600'];

const driverTooltipId = (type: PriceRecommendationStateDriverType) =>
	`recommendation-analysis-${type.toLowerCase()}-tooltip`;

const RenderRecommendationAnalysis: React.FunctionComponent<{
	recommendationState: PriceRecommendationState
}> = ({ recommendationState }) => {
	const drivers = orderRecommendationDrivers(recommendationState.drivers, DRIVER_ORDER);

	return <>
		<div
			className="divide-y divide-gray-200 overflow-hidden rounded-md bg-gray-50 ring-1 ring-black ring-opacity-10">
			<div className="px-3 py-3">
				<h6 className="text-sm font-semibold text-gray-900">Recommendation Analysis</h6>
				<p className="mt-0.5 text-xs text-gray-500">
					Based on {Utilities.snakeCaseToWords(recommendationState.roomMappingType)} — the cheapest room
					type with a recommendation.
				</p>
			</div>
			<div className="space-y-2 divide-y divide-gray-100 text-sm">
				<div className="overflow-x-auto">
					<div className="inline-block min-w-full align-middle">
						<table className="min-w-full divide-y divide-gray-200 text-xs">
							<thead>
							<tr>
								<th scope="col" className="py-2 px-3 text-left font-bold text-gray-900 w-36">Factor</th>
								<th scope="col" className="py-2 px-3 text-left font-bold text-gray-900">Impact on Rate</th>
							</tr>
							</thead>
							<tbody className="divide-y divide-gray-200 bg-white">
							{drivers.map(driver => <RenderDriverRow key={driver.type} driver={driver} />)}
							</tbody>
						</table>
					</div>
				</div>
			</div>
		</div>
		{DRIVER_ORDER.map(type => {
			const details = DRIVER_DETAILS[type];
			return <Tooltip
				key={type}
				id={driverTooltipId(type)}
				openEvents={{ mouseenter: true, focus: true, click: true }}
				closeEvents={{ mouseleave: true, blur: true }}
				globalCloseEvents={{ escape: true, clickOutsideAnchor: true }}
				className="z-50 !rounded-lg !bg-white !p-0 !opacity-100 shadow-xl ring-1 ring-black ring-opacity-5"
				style={{ backgroundColor: 'white' }}>
				<div className="w-72 overflow-hidden rounded-lg bg-white px-4 py-3 text-xs text-gray-600">
					<p className="text-sm font-semibold text-gray-900">{details.label}</p>
					<p className="mt-2 border-t border-gray-200 pt-2 leading-5">{details.description}</p>
					<div className="mt-3 flex items-center gap-x-2 border-t border-gray-200 pt-3">
						<div className="flex gap-x-1">
							{DRIVER_SEGMENT_COLORS.map(color =>
								<span key={color} className={`h-2 w-6 rounded-full ${color}`} />)}
						</div>
						<span className="text-gray-500">None &rarr; Very high</span>
					</div>
				</div>
			</Tooltip>;
		})}
	</>;
};

const RenderNoRecommendationNotice: React.FunctionComponent = () => {
	return <div
		className="divide-y divide-gray-200 overflow-hidden rounded-md bg-gray-50 ring-1 ring-black ring-opacity-10">
		<div className="px-3 py-3"><h6 className="text-sm font-semibold text-gray-900">No Rate Recommendation</h6>
		</div>
		<div className="px-3 py-3 text-sm text-gray-600">
			Our pricing engine has not recommended a rate change for this date. Your current rates remain in effect.
		</div>
	</div>;
};

const PriceTableWrapper: React.FunctionComponent<PropsWithChildren> = ({ children }) => {
	return <div
		className="divide-y divide-gray-200 overflow-hidden rounded-md bg-gray-50 ring-1 ring-black ring-opacity-10">
		<SourceStrip badge="WH" title="WebHotelier base rates"
			description="Review the proposed room rates before you submit." />
		<div className="space-y-2 divide-y divide-gray-100 text-sm">
			<div className="overflow-x-auto">
				<div className="inline-block min-w-full align-middle">
					<table className="w-full table-fixed divide-y divide-gray-200 text-xs">
						<thead>
						<tr>
							<th scope="col" className="py-2 px-1.5 text-left">Rooms</th>
							<th scope="col" className="w-8 py-2 px-1.5 text-left">Avl.</th>
							<th scope="col" className="w-12 py-2 px-1.5 text-left">Base</th>
							<th scope="col" className="w-16 py-2 px-1.5 text-left">Proposed</th>
							<th scope="col" className="w-14 py-2 px-1.5 text-left">Change</th>
						</tr>
						</thead>
						{children}
					</table>
				</div>
			</div>
		</div>
	</div>;
};

const RenderPriceTable: React.FunctionComponent<{
	companyHotel: CompanyHotel,
	architecture: PricingArchitecture
	recommendationState: PriceRecommendationState
	showRecommendation: boolean
}> = ({ companyHotel, architecture, recommendationState, showRecommendation }) => {

	const { availabilityDailyState } = React.useContext(AvailabilityDailyStateContext);
	const rooms = recommendationTableRooms(recommendationState.minimumBasePriceRoomStates);
	if (rooms.length === 0) return null;

	const roomType = Utilities.snakeCaseToWords(recommendationState.roomMappingType);
	const findRoomAvailability = (roomName: string) => {
		const item = availabilityDailyState?.current?.items?.find(item => item.roomName.trim().toLowerCase() === roomName.trim().toLowerCase());
		return item?.availability ?? '-';
	};

	return <tbody className="divide-y divide-gray-200 bg-white">
	{rooms.map((room) => {
		const derivative = architecture.derivatives.find(d => d.pricingRoom.id === room.roomId);
		return <tr key={`room-${room.roomId}`} className={room.isAnchor ? 'bg-sky-50' : undefined}>
			<td className="px-1.5 py-2 font-medium text-gray-700 break-words">
				{room.roomName}
				{room.isAnchor && <span
					className="block font-light text-xs italic text-gray-400">(Base Price - {roomType})</span>}
				{!room.isAnchor && <span
					className="block font-light text-xs italic text-gray-400">(
					{derivative!.rule.type === PricingArchitectureDerivativeRuleType.SAME && 'Equal to base price - ' + roomType}
					{derivative!.rule.type === PricingArchitectureDerivativeRuleType.AMOUNT &&
						<span>{derivative!.rule.value! > 0 ? '+' : ''}{derivative!.rule.value}{Utilities.formatCurrency(companyHotel.settings.main.currency)} from base price - {roomType}</span>}
					{derivative!.rule.type === PricingArchitectureDerivativeRuleType.PERCENTAGE &&
						<span>{derivative!.rule.value! > 0 ? '+' : ''}{derivative!.rule.value}% from base price - {roomType}</span>}
					)</span>}
			</td>
			<td className="px-1.5 py-2 whitespace-nowrap text-gray-700 text-right">
				{findRoomAvailability(room.roomName)}
			</td>
			<td className="px-1.5 py-2 whitespace-nowrap text-gray-700">
				{Utilities.formatAmount(room.basePrice, companyHotel.settings.main.currency, 0)}
			</td>
			<td className="px-1.5 py-2 whitespace-nowrap text-gray-500">
				{showRecommendation
					? Utilities.formatAmount(room.finalPrice, companyHotel.settings.main.currency, 0)
					: <span className="text-gray-400">—</span>}
			</td>
			<td className="px-1.5 py-2 whitespace-nowrap text-gray-500">
				{showRecommendation &&
					<RenderDerivativeDifferenceRate basePrice={room.basePrice} calculatedPrice={room.finalPrice} />}
			</td>
		</tr>;
	})}
	</tbody>;
};

const RenderDerivativeDifferenceRate: React.FunctionComponent<{
	basePrice: number,
	calculatedPrice: number
}> = ({ basePrice, calculatedPrice }) => {
	const symbol = '%';
	const rate = roomPriceChangePercent(basePrice, calculatedPrice);
	if (rate === 0) {
		return null;
	}
	return rate > 0 ? <div className="inline-flex gap-x-0.5 items-center text-green-500">
		<ArrowUpIcon className="w-3 h-3" />
		<span>+{rate}{symbol}</span>
	</div> : <div className="inline-flex gap-x-0.5 items-center text-red-500">
		<ArrowDownIcon className="w-3 h-3" />
		<span>{rate}{symbol}</span>
	</div>;
};

const RenderDriverRow: React.FunctionComponent<{ driver: PriceRecommendationStateDriver }> = ({ driver }) => {
	const level = getDriverImpactLevel(driver.impact);
	const details = DRIVER_DETAILS[driver.type];
	const levelLabel = DRIVER_IMPACT_LEVEL_LABELS[level];

	return <tr>
		<td className="whitespace-nowrap px-3 py-2 font-medium text-gray-700">
			<div className="flex items-center gap-x-1.5">
				<span>{details.label}</span>
				<button
					type="button"
					aria-label={`About ${details.label}`}
					data-tooltip-id={driverTooltipId(driver.type)}
					className="group inline-flex rounded-full focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-1">
					<InformationCircleIcon className="h-4 w-4 text-gray-400 group-hover:text-blue-500" />
				</button>
			</div>
		</td>
		<td className="px-3 py-2">
			<div className="flex items-center gap-x-3">
				<div className="flex h-3.5 items-center gap-x-1">
					{DRIVER_SEGMENT_COLORS.map((color, index) =>
						<span
							key={color}
							className={`h-2 w-6 rounded-full ${index < level ? color : 'bg-gray-200'}`} />)}
				</div>
				<span className={level === 0 ? 'text-xs italic text-gray-400' : 'text-xs text-gray-500'}>
					{levelLabel}
				</span>
			</div>
		</td>
	</tr>;
};


const RenderEditPricingButton: React.FunctionComponent<{
	companyHotel: CompanyHotel,
	architectures: PricingArchitecture[],
	recommendationStates: PriceRecommendationState[],
	onSubmit: () => any,
	statDate: string
}> = ({ companyHotel, recommendationStates, onSubmit, architectures, statDate }) => {
	const { canChangePrices } = usePricingRights(companyHotel);
	const modal = useModal();
	const tools = useMarketStateTools();
	const queryClient = useQueryClient();
	const toaster = useToaster();
	const { t } = useTranslation();
	const directSubmitPending = React.useRef(false);
	const [directSubmitResult, setDirectSubmitResult] = useState<SubmitPricingResultOutput | null>(null);
	const submittedRecommendationStateIds =
		tools.findDailySnapshotState(statDate)?.marketStateDailySnapshot?.submittedRecommendationStateIds ?? [];
	const hasSubmittedRecommendation = recommendationStates.length > 0 && recommendationStates.every(recommendation =>
		submittedRecommendationStateIds.includes(recommendation.id)
	);
	// Direct Submit: the valid, not yet submitted recommendations of the open tab, as they are.
	const submittableStates = recommendationStates.filter(recommendation =>
		recommendation.isValid && !submittedRecommendationStateIds.includes(recommendation.id));
	const rateCount = submittableStates.reduce((count, recommendation) =>
		count + recommendationTableRooms(recommendation.minimumBasePriceRoomStates).length, 0);

	const directSubmitMutation = useMutation({
		mutationFn: PricingAutomationService.submitSelectivePricing,
		onMutate: () => setDirectSubmitResult(null),
		onSuccess: async (result) => {
			if (result.state === SubmitMonthlyPricingResultOutputState.SUCCESS) {
				await queryClient.invalidateQueries({ queryKey: MarketStateGraphQLQueryKeys.all });
				await queryClient.invalidateQueries({ queryKey: PricingAutomationQueryKeys.pricingSubmissions(companyHotel.id) });
				toaster.success('Pricing', 'Pricing submitted successfully!');
				tools.reloadMarketStateForDay(statDate);
				return;
			}
			setDirectSubmitResult(result);
		},
		onError: (error: string) => toaster.error('Pricing', t(error)),
		onSettled: () => { directSubmitPending.current = false; },
	});
	const directSubmitError = directSubmitResult?.errorCode ?? directSubmitResult?.errorMessage;

	if (!canChangePrices || !companyHotel.hasRecommendationCanSubmitPrices()) return null;
	if (hasSubmittedRecommendation)
		return <FormButton state="primary" disabled>Pricing Submitted</FormButton>;

	return <div className="flex w-full flex-col gap-y-3">
		{directSubmitResult !== null && directSubmitResult.blockedItems.length > 0 && <PriceSubmissionBlockedItems
			items={directSubmitResult.blockedItems}
			currency={companyHotel.settings.main.currency}
			onDateSelect={() => modal.open()} />}
		{directSubmitResult !== null && directSubmitResult.blockedItems.length === 0 && directSubmitError !== undefined && directSubmitError !== '' &&
			<Message state="error">{t(directSubmitError, ...(directSubmitResult.errorParams ?? []))}</Message>}
		<div className="flex gap-x-2 justify-end">
			<FormButton state="white" disabled={directSubmitMutation.isPending} onClick={() => modal.open()}>Edit Pricing</FormButton>
			{rateCount > 0 && <FormButton state="primary"
											isLoading={directSubmitMutation.isPending}
											disabled={directSubmitMutation.isPending}
						onClick={() => {
							if (directSubmitPending.current || rateCount === 0) return;
							directSubmitPending.current = true;
							directSubmitMutation.mutate({
																			companyHotelId: companyHotel.id,
																			input: { recommendationIds: submittableStates.map(recommendation => recommendation.id) },
																		}); }}>
				Submit {rateCount} {rateCount === 1 ? 'rate' : 'rates'}
			</FormButton>}
		</div>
		<PricingIntegrationModal ref={modal.ref}
		                         companyHotel={companyHotel}
		                         architectures={architectures}
		                         recommendationStates={recommendationStates}
		                         onSubmit={onSubmit}
		                         statDate={statDate} />
	</div>;
};

export default PriceRecommendationDrawerContents;
